import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Space, Input, Row, Col, Select } from 'antd';
import { Redirect } from 'react-router-dom';

import { CommonFormProps, OnboardingLayout } from '.';
import { createPath } from '~/pages/onboarding/Sider';
import {
  BackButton,
  Form,
  LegalTypeInput,
  BusinessDocumentParametersInputGroup,
} from '~/components';
import { useOnboardingPage, useOnboardingState } from '~/hooks/onboarding';
import { useStatefulForm } from '~/hooks/form';
import { useBusinessDocumentType } from '~/hooks/useHandbook';

const { Title, Text } = Typography;

const CompanyInformation: FC<CommonFormProps> = (props) => {
  const { name } = props;
  const { t } = useTranslation(['onboarding', 'documentTypeParameters']);
  const dt = (key: string): string => t(`translation:${key}`);

  const [pageState, setPageState] = useOnboardingState(name);
  const [countryState] = useOnboardingState('country');

  const onboardingPage = useOnboardingPage(name);

  const { formState, handleFormChange, form } = useStatefulForm(pageState.formData || undefined);

  const countryId = countryState.formData?.country;
  const legalType = formState?.legalType;
  const businessDocumentType = useBusinessDocumentType({
    countryId,
    legalType,
    filterOptions: (item) => !!item.primary,
  });

  const onFinish = (values: any) => {
    setPageState((prev) => ({
      ...prev,
      formData: values,
    }));
    onboardingPage.activateNext();
    onboardingPage.goNext();
  };

  return (
    <OnboardingLayout>
      {pageState.skip && <Redirect to={createPath('personalInfo')} />}
      <Title level={2}>{t('companyInformationHeader')}</Title>
      <Space size="middle" direction="vertical">
        <Text>{t('companyInformationSubtitle')}</Text>
        <Form
          form={form}
          name={name}
          layout="vertical"
          size="large"
          onFinish={onFinish}
          onValuesChange={(_, values: any) => handleFormChange(values)}
        >
          <Form.Item name="legalName" rules={[{ required: true }]}>
            <Input placeholder={dt('companyLegalName')} />
          </Form.Item>
          <Form.Item name="legalType" rules={[{ required: true }]}>
            <LegalTypeInput countryId={countryId} />
          </Form.Item>
          <Form.Item name={['primaryDocument', 'type']} rules={[{ required: true }]}>
            <Select
              placeholder={dt('documentType')}
              options={businessDocumentType.options}
              loading={businessDocumentType.isLoading}
            />
          </Form.Item>
          <BusinessDocumentParametersInputGroup
            nameFn={(n) => ['primaryDocument', n]}
            labelsVariant="placeholder"
            countryId={countryId}
            legalType={formState?.legalType}
            documentType={formState?.primaryDocument?.type}
          />
          <Row gutter={16}>
            <Col>
              <BackButton onClick={onboardingPage.goBack} />
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button type="primary" htmlType="submit">
                  {dt('continue')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Space>
    </OnboardingLayout>
  );
};

export default CompanyInformation;
